import React, { FC, useCallback, useEffect, useState } from 'react';
import firebase from 'firebase/app';
import { Box, Flex, Text, Card, Button } from 'rebass/styled-components';
import { adminApi } from 'util/adminApi';

/**
 * Activity tab: every change to the library and every admin action, newest first,
 * recorded by the server. Deleted records can be put back from here.
 */

type Entry = {
  id: string;
  at: Date | null;
  kind: 'data' | 'admin';
  action: string;
  collection?: string;
  docId?: string;
  title?: string | null;
  changedFields?: string[];
  actor?: { type: string; id: string | null; email?: string | null };
  targetEmail?: string | null;
  from?: string | null;
  to?: string | null;
  restoredAt?: unknown;
  before?: { _omitted?: boolean } | null;
};

const PAGE = 100;
const SINGULAR: Record<string, string> = {
  projects: 'project',
  records: 'record',
  videos: 'video',
  notes: 'note',
  assets: 'asset',
  gallery: 'gallery item',
  images: 'image',
  categories: 'category',
  defaults: 'defaults',
};

const describe = (e: Entry): string => {
  if (e.kind === 'admin') {
    const who = e.targetEmail || e.docId || '';
    switch (e.action) {
      case 'user.create':
        return `Created account ${who} (${e.to})`;
      case 'user.disable':
        return `Disabled ${who}`;
      case 'user.enable':
        return `Enabled ${who}`;
      case 'user.setRole':
        return `Changed ${who} from ${e.from || 'no access'} to ${e.to || 'no access'}`;
      case 'user.delete':
        return `Deleted the sign-in account ${who}`;
      case 'user.resetLink':
        return `Made a password reset link for ${who}`;
      case 'record.restore':
        return `Restored ${SINGULAR[e.collection || ''] || e.collection} ${e.docId}`;
      default:
        return e.action;
    }
  }
  const thing = `${SINGULAR[e.collection || ''] || e.collection} “${e.title || e.docId}”`;
  if (e.action === 'create') return `Added ${thing}`;
  if (e.action === 'delete') return `Deleted ${thing}`;
  return `Edited ${thing}${e.changedFields?.length ? ` (${e.changedFields.join(', ')})` : ''}`;
};

const ActivityLog: FC = () => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [onlyDeletes, setOnlyDeletes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let q = firebase.firestore().collection('activity').orderBy('at', 'desc').limit(PAGE);
      if (onlyDeletes) {
        q = firebase.firestore().collection('activity').where('action', '==', 'delete').orderBy('at', 'desc').limit(PAGE);
      }
      const snap = await q.get();
      setEntries(
        snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, at: data.at ? data.at.toDate() : null } as Entry;
        }),
      );
      const users = await adminApi.listUsers();
      setEmails(Object.fromEntries(users.users.map((u) => [u.uid, u.email || u.uid])));
      setMessage(null);
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [onlyDeletes]);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (e: Entry) => {
    if (!window.confirm(`Put back ${describe(e).replace(/^Deleted /, '')} exactly as it was before it was deleted?`)) return;
    setBusy(e.id);
    try {
      const res = await adminApi.restoreDeleted(e.id);
      setMessage(`Restored ${res.restored}.${res.note ? ' ' + res.note : ''}`);
      await load();
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const who = (e: Entry) => {
    if (!e.actor) return '';
    if (e.actor.email) return e.actor.email;
    if (e.actor.id && emails[e.actor.id]) return emails[e.actor.id];
    if (e.actor.type === 'service_account' || e.actor.type === 'system') return 'System';
    return e.actor.id || e.actor.type;
  };

  return (
    <Box mb={4}>
      <Flex alignItems="baseline" mb={2} sx={{ gap: '12px' }} flexWrap="wrap">
        <Text fontSize={3} fontWeight={1} color="greyDark">
          Activity
        </Text>
        <Text fontSize={0} color="grey3">
          Latest {PAGE} changes, recorded by the server. Nobody can edit or remove this log from the app.
        </Text>
        <Box flex={1} />
        <Button
          onClick={() => setOnlyDeletes(!onlyDeletes)}
          py={1}
          px={2}
          fontSize={0}
          sx={{ bg: onlyDeletes ? 'primary' : 'white', color: onlyDeletes ? 'white' : 'greyDark', border: '1px solid', borderColor: 'grey2' }}
        >
          {onlyDeletes ? 'Showing deletions' : 'Show deletions only'}
        </Button>
      </Flex>
      {message && (
        <Card p={3} mb={3} bg="white" sx={{ borderLeft: '4px solid', borderColor: 'secondary' }}>
          <Text fontSize={1}>{message}</Text>
        </Card>
      )}
      <Card p={2} bg="white" sx={{ boxShadow: 'main', borderRadius: 4, overflowX: 'auto' }}>
        {loading && !entries.length && (
          <Text p={2} fontSize={1} color="grey3">
            Loading…
          </Text>
        )}
        {!loading && !entries.length && (
          <Text p={2} fontSize={1} color="grey3">
            Nothing recorded yet. Changes appear here from the moment the activity log was switched on.
          </Text>
        )}
        {entries.map((e) => (
          <Flex
            key={e.id}
            py={2}
            px={2}
            alignItems="center"
            fontSize={1}
            color="greyDark"
            sx={{ borderBottom: '1px solid', borderColor: 'grey1', minWidth: 640, gap: '12px' }}
          >
            <Box width={150} fontSize={0} color="grey3">
              {e.at ? e.at.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' } as any) : '…'}
            </Box>
            <Box flex={3}>{describe(e)}</Box>
            <Box flex={1.5} fontSize={0} color="grey3" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {who(e)}
            </Box>
            <Box width={110}>
              {e.kind === 'data' && e.action === 'delete' && !e.restoredAt && e.before && !e.before._omitted && (
                <Button onClick={() => restore(e)} disabled={busy !== null} py={1} px={2} fontSize={0} variant="primary">
                  {busy === e.id ? 'Restoring…' : 'Restore'}
                </Button>
              )}
              {!!e.restoredAt && (
                <Text fontSize={0} color="green">
                  Restored
                </Text>
              )}
            </Box>
          </Flex>
        ))}
      </Card>
    </Box>
  );
};

export default ActivityLog;
